export interface WeeklyRangePlanRow {
  week_start: string
  week_end: string
  week_len_days?: number | null
  weekly_leads_plan?: number | null
  weekly_appointments_plan?: number | null
  weekly_new_visitors_plan?: number | null
  weekly_new_sales_plan?: number | null
  weekly_new_subscriptions_count_plan?: number | null
}

export interface FunnelPlanRangeResult {
  leads: number
  appointments: number
  visits: number
  sales: number
  bookingConversionPlan: number
  visitConversionPlan: number
  salesConversionPlan: number
}

export function formatDateLocalIso(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export function buildFunnelPlanFromWeeklyRows(
  weeklyPlans: WeeklyRangePlanRow[] | null | undefined,
  startDate: Date,
  endDate: Date
): FunnelPlanRangeResult | null {
  if (!weeklyPlans || weeklyPlans.length === 0) return null

  const weightedPlan = {
    leads: 0,
    appointments: 0,
    visits: 0,
    sales: 0,
    weightedLeadBase: 0,
    weightedAppointmentBase: 0,
    weightedVisitBase: 0
  }

  for (const week of weeklyPlans) {
    const weekStart = new Date(week.week_start)
    const weekEnd = new Date(week.week_end)
    const overlapStart = new Date(Math.max(startDate.getTime(), weekStart.getTime()))
    const overlapEnd = new Date(Math.min(endDate.getTime(), weekEnd.getTime()))
    const overlapDays = Math.floor((overlapEnd.getTime() - overlapStart.getTime()) / (1000 * 60 * 60 * 24)) + 1
    if (overlapDays <= 0) continue

    const weekLen = week.week_len_days || 7
    const ratio = overlapDays / weekLen

    const leadsPlan = (week.weekly_leads_plan || 0) * ratio
    const appointmentsPlan = (week.weekly_appointments_plan || 0) * ratio
    const visitorsPlan = (week.weekly_new_visitors_plan || 0) * ratio
    const salesPlan = ((week.weekly_new_sales_plan || 0) + (week.weekly_new_subscriptions_count_plan || 0)) * ratio

    weightedPlan.leads += leadsPlan
    weightedPlan.appointments += appointmentsPlan
    weightedPlan.visits += visitorsPlan
    weightedPlan.sales += salesPlan
    weightedPlan.weightedLeadBase += leadsPlan
    weightedPlan.weightedAppointmentBase += appointmentsPlan
    weightedPlan.weightedVisitBase += visitorsPlan
  }

  const bookingConversionPlan = weightedPlan.weightedLeadBase > 0
    ? (weightedPlan.appointments / weightedPlan.weightedLeadBase) * 100
    : 0
  const visitConversionPlan = weightedPlan.weightedAppointmentBase > 0
    ? (weightedPlan.visits / weightedPlan.weightedAppointmentBase) * 100
    : 0
  const salesConversionPlan = weightedPlan.weightedVisitBase > 0
    ? (weightedPlan.sales / weightedPlan.weightedVisitBase) * 100
    : 0

  return {
    leads: Math.round(weightedPlan.leads),
    appointments: Math.round(weightedPlan.appointments),
    visits: Math.round(weightedPlan.visits),
    sales: Math.round(weightedPlan.sales),
    bookingConversionPlan,
    visitConversionPlan,
    salesConversionPlan
  }
}
